// Kosakata kategori produk — SATU sumber untuk seluruh frontend (Sales.jsx,
// InputNota.jsx, FinancePersediaan.jsx). Kategori menentukan akun persediaan/
// HPP/pendapatan yang kena di pembukuan — lihat
// backend/routers/finance/_shared.py (KATEGORI_KANONIK, ALIAS_KATEGORI,
// KATEGORI_PENJUALAN) dan memory/MIGRASI-KATEGORI-23AGU.md. SALINAN INI HARUS
// SAMA PERSIS dengan backend — kalau backend menambah/mengubah kunci,
// perbarui di sini juga.
//
// Dulu ada DUA salinan identik dari deriveKode/deriveKategori (Sales.jsx dan
// InputNota.jsx) yang bisa perlahan berpisah jalan — pola yang sama persis
// dengan next_code/_next_code (lihat CLAUDE.md, "Duplikasi helper melahirkan
// penyimpangan"). Sekarang satu sumber.

// Sebelas kategori kanonik (barang fisik — punya akun persediaan+HPP+
// pendapatan) — cermin KATEGORI_KANONIK backend. Urutan mengikuti tabel di
// MIGRASI-KATEGORI-23AGU.md.
export const KATEGORI_KANONIK = [
  "handphone_baru", "handphone_second",
  "tab_baru", "tab_second",
  "laptop_baru", "laptop_second",
  "wearable_baru", "wearable_second",
  "original_acc", "accessories",
  "sim_sticker",
];

// Kunci LAMA (sebelum restrukturisasi 24 Agu 2026) -> kunci kanonik dengan
// akun yang IDENTIK — dipertahankan SELAMANYA supaya nota/produk lama tetap
// terbaca benar (cermin ALIAS_KATEGORI backend). JANGAN tawarkan kunci ini
// sebagai pilihan BARU di dropdown manapun — hanya untuk membaca dokumen lama.
export const ALIAS_KATEGORI = {
  unit_baru: "handphone_baru",
  unit_second: "handphone_second",
  aksesoris: "accessories",
};

// Label Indonesia per kunci kanonik + "aktivasi_imei" (jasa, bukan barang
// fisik, tidak ikut KATEGORI_KANONIK karena tidak punya akun persediaan —
// tapi tetap kategori item nota yang sah, lihat KATEGORI_PENJUALAN backend).
const LABEL = {
  handphone_baru: "Handphone Baru",
  handphone_second: "Handphone Second",
  tab_baru: "Tab Baru",
  tab_second: "Tab Second",
  laptop_baru: "Laptop Baru",
  laptop_second: "Laptop Second",
  wearable_baru: "Wearable Baru",
  wearable_second: "Wearable Second",
  original_acc: "Original Acc",
  accessories: "Accessories",
  sim_sticker: "SIM Sticker",
  aktivasi_imei: "Aktivasi IMEI",
};

// Warna badge per kategori (dipakai FinancePersediaan) — hanya memakai warna
// yang sudah dipakai di file ini/sekitarnya (emerald/amber/sky/slate),
// TIDAK menambah palet baru (aturan token desain). Baru = emerald, Second =
// amber, non-gadget (Accessories/Original Acc) = sky, SIM Sticker/jasa = slate.
const WARNA = {
  handphone_baru: "emerald", handphone_second: "amber",
  tab_baru: "emerald", tab_second: "amber",
  laptop_baru: "emerald", laptop_second: "amber",
  wearable_baru: "emerald", wearable_second: "amber",
  original_acc: "sky", accessories: "sky",
  sim_sticker: "slate", aktivasi_imei: "slate",
};

// Dropdown kategori item nota/penjualan (Sales.jsx, InputNota.jsx) — HANYA
// kunci kanonik BARU (+ "aktivasi_imei", jasa non-inventory yang sudah lama
// ada di dropdown ini). Kunci lama (unit_baru/unit_second/aksesoris) SENGAJA
// tidak ditawarkan di sini — kalau tetap ditawarkan, staf akan terus membuat
// nota baru bergaya lama selamanya. Dokumen LAMA yang sudah memakai kunci
// lama tetap terbaca lewat ALIAS_KATEGORI (backend & labelKategori di bawah),
// daftar pilihan ini tidak memengaruhi itu.
export const KATEGORI_ITEM = [
  ...KATEGORI_KANONIK.map((v) => ({ v, l: LABEL[v] })),
  { v: "aktivasi_imei", l: LABEL.aktivasi_imei },
];

// Default kategori baris kosong/baru — kanonik, BUKAN alias, supaya baris
// yang tidak pernah disentuh kasir pun ikut kosakata baru.
export const KATEGORI_DEFAULT = "handphone_baru";

// Label kategori APA PUN (kanonik, alias lama, atau kunci tak dikenal) —
// dipakai untuk MENAMPILKAN kategori yang sudah tersimpan (termasuk dokumen
// lama), jadi HARUS mengenali alias juga — beda dengan KATEGORI_ITEM yang
// sengaja tidak menawarkannya sebagai pilihan baru.
export function labelKategori(kategori) {
  const k = (kategori || "").trim();
  if (!k) return "-";
  if (LABEL[k]) return LABEL[k];
  const kanon = ALIAS_KATEGORI[k];
  if (kanon && LABEL[kanon]) return LABEL[kanon];
  // Kunci tak dikenal (mis. "servis", atau kategori masa depan yang belum
  // ditambahkan di sini) — tampilkan apa adanya alih-alih hilang diam-diam.
  return k.split("_").filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
}

// Kunci kanonik untuk kategori APA PUN — cermin `normalisasi_kategori`
// backend. Dipakai saat menaruh nilai tersimpan ke dalam <select>: daftar
// pilihannya (KATEGORI_ITEM) sengaja hanya berisi kunci kanonik, jadi baris
// nota LAMA yang bernilai "unit_baru" tidak cocok dengan option mana pun dan
// dropdown-nya tampil KOSONG — kasir mengira kategorinya belum diisi.
//
// Efek sampingnya disengaja: begitu nota lama disimpan ulang, kategorinya ikut
// tertulis dengan kunci baru. Akun COA-nya sama persis (alias dan kanonik
// menunjuk akun yang identik), jadi tidak ada rupiah yang berpindah.
export function normalKategori(kategori) {
  const k = (kategori || "").trim();
  if (!k) return "";
  return ALIAS_KATEGORI[k] || k;
}

export function warnaKategori(kategori) {
  const k = (kategori || "").trim();
  return WARNA[k] || WARNA[ALIAS_KATEGORI[k]] || "slate";
}

// ==================== Auto-derive KODE & KATEGORI dari TEKS BEBAS ====================
// HANYA berlaku untuk baris yang benar-benar diketik manual TANPA SKU (jasa,
// titipan, barang di luar katalog). Baris yang tertaut SKU tidak pernah lewat
// sini — kategorinya diambil apa adanya dari master produk (lihat
// ItemNamaField.pilih() di Sales.jsx/InputNota.jsx), TIDAK ditebak dari nama.
//
// KENAPA tebakan ini SENGAJA TIDAK diperluas untuk mengenali Tab/Laptop/
// Wearable/Original Acc dari teks: nama produk gadget sekarang dibentuk
// OTOMATIS dan diawali MEREK ("Apple iPhone 15 128GB Black"), bukan lagi kata
// yang menyiratkan kategorinya. Menambah lebih banyak tebakan kata kunci di
// sini hanya memperbesar peluang salah kategori -> salah akun, persis masalah
// yang sedang dicegah tugas ini (lihat memory/MIGRASI-KATEGORI-23AGU.md).
// Perilaku pencocokan di bawah PERSIS sama dengan sebelum migrasi kategori —
// hanya OUTPUT-nya yang memakai kunci kanonik baru, bukan alias lama.
const HP_BRANDS = new Set([
  "iphone", "samsung", "xiaomi", "oppo", "vivo", "realme", "redmi", "poco", "infinix", "tecno",
  "nokia", "huawei", "honor", "asus", "sony", "apple", "lg", "motorola", "sharp", "zte", "meizu",
  "advan", "evercoss", "polytron", "itel", "lenovo", "alcatel", "hp",
]);
const ACC_KEYWORDS = new Set([
  "case", "casing", "charger", "cas", "kabel", "cable", "tws", "earbud", "earbuds", "earphone",
  "headset", "headphone", "airpods", "airpod", "powerbank", "screen", "tempered", "antigores",
  "anti", "cover", "adapter", "usb", "speaker", "holder", "stand", "sim", "memory", "memori",
  "sd", "otg", "dock", "gimbal", "tripod", "stylus", "aksesoris", "acc", "glass", "protector",
  "mouse", "keyboard", "tas", "sarung", "strap", "band", "ring", "popsocket", "selfie", "lens",
]);
const SERVICE_KEYWORDS = new Set([
  "service", "servis", "perbaikan", "ganti", "repair", "lcd", "sparepart", "install", "garansi", "upgrade",
]);

// Auto-derive kode dari kata pertama nama barang (dropdown poin disembunyikan
// di UI, dipakai server hanya sebagai cadangan kalau kategori kosong — lihat
// sales_posting.py::kategori_item).
export const deriveKode = (nama_barang) => {
  const first = ((nama_barang || "").trim().split(/\s+/)[0] || "").toLowerCase();
  if (!first) return "HP"; // default
  if (SERVICE_KEYWORDS.has(first)) return "Other";
  if (ACC_KEYWORDS.has(first)) return "ACC";
  if (HP_BRANDS.has(first)) return "HP";
  // Fallback: infer from any part of the string for common patterns
  const s = (nama_barang || "").toLowerCase();
  if (/(case|charger|kabel|tws|earbud|headset|powerbank|tempered|adapter|usb|speaker|holder|stand|otg|glass|mouse|keyboard)/.test(s)) return "ACC";
  if (/(service|servis|perbaikan|repair|lcd|sparepart)/.test(s)) return "Other";
  return "HP";
};

// Saran kategori untuk baris TANPA SKU — cuma tiga kemungkinan (persis
// perilaku lama), TIDAK pernah menghasilkan Tab/Laptop/Wearable/Original Acc.
export const deriveKategoriTeksBebas = (nama_barang) => {
  const s = (nama_barang || "").toLowerCase();
  if (/(second|bekas|seken|used)/.test(s)) return "handphone_second";
  if (deriveKode(nama_barang) === "ACC") return "accessories";
  return "handphone_baru";
};
